/**
 * Helpers for the screens that choose which student facts a document prints
 * (#574): result PDF, ID card and admission-letter placeholders. Keys come
 * from the studentDocumentFields query — a built-in field such as
 * "nationality", or "custom:<key>" for a school custom field (state of origin
 * and LGA are custom fields, e.g. "custom:state_of_origin").
 */

export interface DocumentFieldOption {
  key: string;
  label: string;
}

export const CUSTOM_FIELD_KEY_PREFIX = 'custom:';

/** The CR80 card has room for three short rows under Programme/Department/Level. */
export const MAX_ID_CARD_EXTRA_FIELDS = 3;

/**
 * Drop saved keys the school can no longer print (a custom field was retired
 * or deleted) so saving the settings does not fail on a stale selection.
 * Keeps the saved order and removes duplicates.
 */
export function pruneDocumentFields(selected: readonly string[] | null | undefined, options: readonly DocumentFieldOption[]): string[] {
  const unique = [...new Set(selected ?? [])];
  // The catalog always has built-in fields, so an empty list means it has not
  // loaded yet — keep the selection rather than wipe it.
  if (options.length === 0) return unique;
  const known = new Set(options.map((option) => option.key));
  return unique.filter((key) => known.has(key));
}

/**
 * First server validation message for a list attribute. The backend keys an
 * unknown entry by index ("input.student_info_fields.2"), which the generic
 * useFormErrors lookup does not match.
 */
export function listFieldError(errors: Record<string, string[]>, attribute: string): string {
  const key = Object.keys(errors).find((name) => name === attribute || name.startsWith(`${attribute}.`)
    || name.endsWith(`.${attribute}`) || name.includes(`.${attribute}.`));
  return key ? errors[key]?.[0] ?? '' : '';
}

/** Admission-letter tokens for the school's custom fields: "custom.<key>". */
export function customLetterTokens(options: readonly DocumentFieldOption[]): Array<{ token: string; label: string }> {
  return options
    .filter((option) => option.key.startsWith(CUSTOM_FIELD_KEY_PREFIX))
    .map((option) => ({ token: `custom.${option.key.slice(CUSTOM_FIELD_KEY_PREFIX.length)}`, label: option.label }));
}
